import { ComponentConfig, ComponentType, LayoutTypes } from "../services/types/ComponentTypes";

export type AttendanceAdminTab = "my" | "team";

const TEAM_ONLY_FIELD_ATTRS = new Set([
  "employee_id",
  "employee_name",
  "branch_name",
]);

const ATTENDANCE_ADMIN_TABS: ComponentConfig = {
  componentType: ComponentType.CUSTOM,
  customType: "attendanceAdminTabs",
} as ComponentConfig;

const ATTENDANCE_MONTH_FILTER: ComponentConfig = {
  componentType: ComponentType.CUSTOM,
  customType: "monthFilter",
  Json_Attribute: "month_year",
  name: "attendance_month_filter",
  props: {
    filterField: "month / year",
  },
} as ComponentConfig;

function isCheckInOutNode(node: any): boolean {
  const t = String(node?.componentType ?? "").toLowerCase();
  return (
    t === "check_in_out_component" ||
    t === "checkinout" ||
    t === "checkinoutcomponent"
  );
}

function containsCheckInOutOnly(node: any): boolean {
  if (!node) return false;
  if (isCheckInOutNode(node)) return true;
  const kids: any[] = Array.isArray(node.childComponents) ? node.childComponents : [];
  if (kids.length === 0) return false;
  return kids.every(
    (k) =>
      containsCheckInOutOnly(k) ||
      (Array.isArray(k?.childComponents) && containsCheckInOutOnly(k))
  );
}

function enableAdminCheckIn(node: any): any {
  if (!node || typeof node !== "object") return node;
  if (isCheckInOutNode(node)) {
    return { ...node, visibility: [1, 2] };
  }
  if (Array.isArray(node.childComponents)) {
    return {
      ...node,
      childComponents: node.childComponents.map((child: any) => enableAdminCheckIn(child)),
    };
  }
  return node;
}

function isSummaryListNode(node: any): boolean {
  const t = String(node?.componentType ?? "").toLowerCase();
  return t === "summerylist" || t === "summarylist" || t === "summary_list";
}

function patchSummaryListForTab(node: any, adminMyTab: boolean): any {
  if (!isSummaryListNode(node)) return node;
  const fields = Array.isArray(node.fields) ? node.fields : [];
  const nextFields = adminMyTab
    ? fields.filter(
        (f: any) => !TEAM_ONLY_FIELD_ATTRS.has(String(f?.Json_Attribute ?? "").toLowerCase())
      )
    : fields;
  return {
    ...node,
    View: node.View || "view_attendance_summary",
    fields: nextFields,
  };
}

function hasAttendanceAdminTabs(components: ComponentConfig[]): boolean {
  const walk = (nodes: ComponentConfig[]): boolean => {
    for (const node of nodes) {
      const n = node as any;
      if (
        String(n.componentType ?? "").toLowerCase() === "custom" &&
        String(n.customType ?? "").toLowerCase() === "attendanceadmintabs"
      ) {
        return true;
      }
      if (Array.isArray(n.childComponents) && walk(n.childComponents)) {
        return true;
      }
    }
    return false;
  };
  return walk(components);
}

function injectAttendanceAdminTabs(components: ComponentConfig[]): ComponentConfig[] {
  if (hasAttendanceAdminTabs(components)) return components;

  let injected = false;
  const walk = (nodes: ComponentConfig[]): ComponentConfig[] =>
    nodes.map((node) => {
      const n = node as any;
      if (!Array.isArray(n.childComponents) || n.childComponents.length === 0) {
        return node;
      }

      const childType = String(n.componentType ?? "").toLowerCase();
      if (!injected && (childType === "group" || childType === "screengroup")) {
        injected = true;
        return {
          ...n,
          childComponents: [ATTENDANCE_ADMIN_TABS, ...walk(n.childComponents)],
        } as ComponentConfig;
      }

      return {
        ...n,
        childComponents: walk(n.childComponents),
      } as ComponentConfig;
    });

  return walk(components);
}

/** Keep only the first MY/TEAM tab bar (drop duplicates from config + inject). */
function dedupeAttendanceAdminTabs(components: ComponentConfig[]): ComponentConfig[] {
  let seen = false;

  const walk = (nodes: ComponentConfig[]): ComponentConfig[] => {
    const result: ComponentConfig[] = [];
    for (const node of nodes) {
      const n = node as any;
      const isTabs =
        String(n.componentType ?? "").toLowerCase() === "custom" &&
        String(n.customType ?? "").toLowerCase() === "attendanceadmintabs";

      if (isTabs) {
        if (seen) continue;
        seen = true;
      }

      if (Array.isArray(n.childComponents)) {
        result.push({ ...n, childComponents: walk(n.childComponents) } as ComponentConfig);
      } else {
        result.push(node);
      }
    }
    return result;
  };

  return walk(components);
}

function replaceDateFilterWithMonthFilter(components: ComponentConfig[]): ComponentConfig[] {
  const walk = (nodes: ComponentConfig[]): ComponentConfig[] =>
    nodes.reduce<ComponentConfig[]>((acc, node) => {
      const n = node as any;
      const compType = String(n.componentType ?? "").trim().toLowerCase();
      const customType = String(n.customType ?? "").trim().toLowerCase();

      if (compType === "custom" && customType === "datefilter") {
        acc.push(ATTENDANCE_MONTH_FILTER);
        return acc;
      }

      if (Array.isArray(n.childComponents)) {
        acc.push({ ...n, childComponents: walk(n.childComponents) } as ComponentConfig);
        return acc;
      }

      acc.push(node);
      return acc;
    }, []);

  return walk(components);
}

/**
 * Screen 2200: MY/TEAM admin attendance — inject tabs, month filter, check-in visibility, team columns.
 */
export function patchAttendanceSummaryScreenConfig(
  components: ComponentConfig[],
  options: { adminMyTab: boolean; roleId: number | null }
): ComponentConfig[] {
  const { adminMyTab, roleId } = options;
  let next = dedupeAttendanceAdminTabs(components);
  next = injectAttendanceAdminTabs(next);
  next = replaceDateFilterWithMonthFilter(next);

  const walk = (nodes: ComponentConfig[]): ComponentConfig[] =>
    nodes.reduce<ComponentConfig[]>((acc, comp) => {
      const c: any = comp as any;
      const compType = String(c.componentType ?? "").trim().toLowerCase();
      const customType = String(c.customType ?? "").trim().toLowerCase();

      if (compType === "custom" && customType === "datefilter") {
        return acc;
      }

      if (roleId !== 2 && !adminMyTab && containsCheckInOutOnly(c)) {
        return acc;
      }

      let nextComp: any = c;
      if (adminMyTab && containsCheckInOutOnly(c)) {
        nextComp = enableAdminCheckIn(c);
      }
      if (isSummaryListNode(nextComp)) {
        nextComp = patchSummaryListForTab(nextComp, adminMyTab);
      }

      if (Array.isArray(nextComp.childComponents)) {
        const nextChildren = walk(nextComp.childComponents);
        if (nextChildren !== nextComp.childComponents || nextComp !== c) {
          acc.push({ ...nextComp, childComponents: nextChildren } as ComponentConfig);
        } else {
          acc.push(comp);
        }
        return acc;
      }

      acc.push(nextComp !== c ? nextComp : comp);
      return acc;
    }, []);

  return dedupeAttendanceAdminTabs(walk(next));
}

export function resolveAttendanceScope(
  roleId: number | null,
  adminTab: AttendanceAdminTab
): AttendanceAdminTab {
  if (roleId === 2) return "my";
  return adminTab;
}

export function monthYearToDateRange(monthYear: string): { start_date: string; end_date: string } {
  const trimmed = String(monthYear || "").trim();
  const match = trimmed.match(/^(\d{4})-(\d{2})$/);
  if (!match) {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { start_date: fmt(start), end_date: fmt(end) };
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { start_date: fmt(start), end_date: fmt(end) };
}
